import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router-dom";
import {
  Paper,
  Grid,
  Typography,
  Button,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { makeStyles } from "@mui/styles";
import {
  removeStrategy,
  fetchPerformanceData,
  fetchStrategies,
  fetchPublicStrategies,
  fetchDrawdownData,
  setCalculated,
  copyStrategy,
  setSettings,
  fetchStrategyById,
  cacheAssetDetails,
} from "../../features/strategySlice";
import StrategySettings from "./StrategySettings";
import StrategyContent from "./StrategyContent";
import { Chart, registerables } from "chart.js";
import investImage from "../../images/investSettings.png";
import { getDeterministicAssetColors } from "./utils";
import { axiosInstance } from "../../axiosConfig";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import theme from "../../theme";

Chart.register(...registerables);

const useStyles = makeStyles((theme) => ({
  head: {
    background: "#eff0f6",
    backgroundSize: "100%",
  },

  fixedTabs: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    zIndex: 1000,
    borderBottom: "1px solid #ddd",
  },
  content: {
    paddingTop: "48px",
  },
  tabs: {
    "& button": {
      color: "#008ED1",
      textTransform: "capitalize",
    },
  },
}));

const StrategyDetails = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const strategies = useSelector((state) => state.strategy.strategies);
  const drawdownData = useSelector((state) => state.strategy.drawdownData);
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();

  const publicStrategies = useSelector(
    (state) => state.strategy.publicStrategies
  );

  const performanceData = useSelector(
    (state) => state.strategy.performanceData?.performance
  );
  const assets = useSelector((state) => state.strategy.assets);
  const assetDetailsCache = useSelector((state) => state.strategy.assetDetailsCache);

  const strategy = useMemo(() => {
    const foundStrategy = strategies.find((s) => s.id === parseInt(id)) ||
                         publicStrategies.find((s) => s.id === parseInt(id));
    return foundStrategy;
  }, [strategies, publicStrategies, id]);

  const settings = useSelector((state) => state.strategy.settings);

  // Add fallback data when strategy is not found or still loading
  const fallbackStrategy = useMemo(() => ({
    id: parseInt(id),
    name: "Loading Strategy...",
    assets: [],
    start_year: 2010,
    end_year: 2020
  }), [id]);

  // Use fallback strategy if no strategy found
  const currentStrategy = strategy || fallbackStrategy;

  useEffect(() => {
    // Set initial dates when strategy data is loaded with start_dates and end_dates
    if (currentStrategy && currentStrategy.start_dates && currentStrategy.end_dates) {
      const currency = settings.currency || 'GBP';
      const startDate = currentStrategy.start_dates[currency];
      const endDate = currentStrategy.end_dates[currency];

      // Always update dates if they exist in strategy data
      if(startDate && endDate) {
        dispatch(setSettings({
          ...settings,
          startDate,
          endDate
        }));

        // Auto-trigger calculate when default dates are loaded
        // This will automatically fetch performance, drawdown, risk&return, and cladogram data
        dispatch(setCalculated(true));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStrategy, settings.currency, dispatch]);

  const tabsRef = useRef(null);
  const [settingsExpanded, setSettingsExpanded] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [isFixed, setIsFixed] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const calculated = useSelector((state) => state.strategy.calculated);

  const zoomOptions = {
    plugins: {
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "xy",
        },
        pan: {
          enabled: true,
          mode: "xy",
        },
      },
      tooltip: {
        callbacks: {
          title: function (tooltipItems) {
            const item = tooltipItems[0];
            const assetIndex = item.dataIndex;
            const asset = currentStrategy?.assets?.[assetIndex];

            if (!asset) return "Unknown Asset";

            const ticker = asset.ticker || asset.asset_ticker || item.label;

            const cachedAsset = assetDetailsCache[ticker];
            let description = null;

            if (cachedAsset?.description && cachedAsset.description !== "undefined") {
              description = cachedAsset.description;
            } else if (asset.asset_description && asset.asset_description !== "undefined") {
              description = asset.asset_description;
            } else if (asset.description && asset.description !== "undefined") {
              description = asset.description;
            }

            if (description) {
              return `${description} (${ticker})`;
            } else {
              return ticker;
            }
          },

          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            const assetIndex = tooltipItem.dataIndex;
            const asset = currentStrategy?.assets?.[assetIndex];

            let result = [];

            if (asset) {
              const ticker = asset.ticker || asset.asset_ticker || tooltipItem.label;
              const cachedAsset = assetDetailsCache[ticker];
              const assetType = cachedAsset?.type || asset.asset_type;

              if (assetType && assetType !== "undefined") {
                result.push(`Type: ${assetType}`);
              }
            }

            result.push(`${value.toFixed(0)}%`);

            return result;
          },

          footer: function() {
            return [];
          }
        },
      },
    },
  };

  useEffect(() => {
    // This effect runs ONLY when the user clicks "Calculate"
    if (calculated && currentStrategy && currentStrategy.id && settings.startDate && settings.endDate) {
      const startDate = settings.startDate;
      const endDate = settings.endDate;

      // Create separate parameter objects for performance and drawdown
      let performanceParams = {
        id: currentStrategy.id,
        start_date: startDate,
        end_date: endDate,
        currency: settings.currency || 'GBP',
        rebalancing_frequency_in_months: settings.rebalancingPeriod || 0,
        investment_amount: parseFloat(settings.investmentAmount || 1000)
        // use_nominal_data will be handled internally by fetchPerformanceData
      };

      let drawdownParams = {
        id: currentStrategy.id,
        start_date: startDate,
        end_date: endDate,
        currency: settings.currency || 'GBP',
        rebalancing_frequency_in_months: settings.rebalancingPeriod || 0,
        investment_amount: parseFloat(settings.investmentAmount || 1000)
        // No use_nominal_data for drawdown
      };

      if (settings.transactionFeeType !== "-" && settings.transactionFeeValue) {
        let adjustedTransactionFeeAmount = parseFloat(settings.transactionFeeValue);
        const isPercentage = settings.transactionFeeType === "percentage" || settings.transactionFeeType === "%";

        if (isPercentage) {
          adjustedTransactionFeeAmount = adjustedTransactionFeeAmount / 100;
        }

        performanceParams.transaction_fee_amount = adjustedTransactionFeeAmount;
        performanceParams.transaction_fee_type = isPercentage ? "percentage" : "currency";

        drawdownParams.transaction_fee_amount = adjustedTransactionFeeAmount;
        drawdownParams.transaction_fee_type = isPercentage ? "percentage" : "currency";
      }


      try {
        dispatch(fetchDrawdownData(drawdownParams));
        dispatch(fetchPerformanceData(performanceParams));
      } catch (error) {
        console.warn('Error dispatching data fetch:', error);
      }


      dispatch(setCalculated(false));
    }
  }, [calculated, currentStrategy, settings, dispatch]);

  useEffect(() => {
    let previousScrollY = window.scrollY;

    const handleScroll = () => {
      if (tabsRef.current) {
        const rect = tabsRef.current.getBoundingClientRect();
        const currentScrollY = window.scrollY;

        if (rect.top <= 0 && currentScrollY > previousScrollY) {
          setIsFixed(true);
        } else if (currentScrollY < previousScrollY) {
          setIsFixed(false);
        }

        previousScrollY = currentScrollY;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const sectionId = [
      "overview",
      "performance",
      "drawdown",
      "risk-return",
      "cladogram",
      "monte-carlo",
    ][newValue];
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -70;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const strategyOverviewData = useMemo(() => {
    return {
      labels: currentStrategy?.assets?.map((asset) => asset.asset_ticker || asset.ticker) || [],
      datasets: [
        {
          data: currentStrategy?.assets?.map((asset) => parseFloat(asset.weight) * 100) || [],
          backgroundColor: getDeterministicAssetColors(currentStrategy?.assets, currentStrategy?.id) || [],
        },
      ],
    };
  }, [currentStrategy?.id, currentStrategy?.assets]);

  const strategyPerformanceData = {
    labels: performanceData?.performance_data?.map((item) => item.date) || [],
    datasets: [
      {
        label: "Strategy Performance",
        data:
          performanceData?.performance_data?.map((item) => item.performance) ||
          [],
        borderColor: "rgba(75,192,192,1)",
        backgroundColor: "rgba(75,192,192,0.4)",
      },
    ],
  };

  const monteCarloData = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Monte Carlo Simulation",
        data: [10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
        backgroundColor: "rgba(75,192,192,0.4)",
        borderColor: "rgba(75,192,192,1)",
      },
    ],
  };

  const monteCarloDataNew = {
    labels: Array.from({ length: 12 }, (_, i) => i + 1),
    datasets: Array.from({ length: 100 }, (_, i) => ({
      label: `Simulation ${i + 1}`,
      data: Array.from({ length: 12 }, () => Math.random() * 100),
      borderColor: `rgba(${Math.random() * 255},${Math.random() * 255},${
        Math.random() * 255
      },0.5)`,
      backgroundColor: "transparent",
      borderWidth: 1,
      pointRadius: 0,
    })),
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
        "Are you sure you want to delete this strategy?"
    );
    if (!confirmed) return;

    try {
        await axiosInstance.delete(`/strategies/${id}/`);
        dispatch(removeStrategy(parseInt(id)));
        navigate("/strategies");
    } catch (error) {
        console.error("Delete error details:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
        });
    }
};

const handleCopy = async () => {
    if (!currentStrategy || !currentStrategy.id) {
      setSnackbarMessage("Strategy not found");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const copiedStrategy = await dispatch(copyStrategy(currentStrategy)).unwrap();

        await dispatch(fetchStrategies());

        setSnackbarMessage("Strategy copied successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        navigate(`/strategy/${copiedStrategy.id}`);
    } catch (error) {
        console.error("Error copying strategy:", error);
        setSnackbarMessage("Failed to copy strategy");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
    }
};

useEffect(() => {
    const ensureAssetDetailsAreCached = async (assets) => {
      if (!assets || assets.length === 0) {
        return;
      }

      const tickersToFetch = assets
        .map(asset => asset.ticker)
        .filter(ticker => ticker && !assetDetailsCache[ticker]);

      if (tickersToFetch.length === 0) {
        return;
      }

      const fetchPromises = tickersToFetch.map(ticker =>
        axiosInstance.get('/search', { params: { search_term: ticker.split('.')[0] } })
      );

      try {
        const responses = await Promise.all(fetchPromises);
        responses.forEach(response => {
          const apiData = Array.isArray(response.data) ? response.data : [];
          if (apiData.length > 0) {
            // Find the best match from search results
            const bestMatch = apiData[0]; // Assume first result is the best
            let fullTicker = bestMatch.Code;
            if (bestMatch.Exchange && !bestMatch.Code.includes('.')) {
              fullTicker = `${bestMatch.Code}.${bestMatch.Exchange}`;
            } else if (!bestMatch.Exchange && !bestMatch.Code.includes('.')) {
              fullTicker = `${bestMatch.Code}.US`;
            }

            const assetToCache = {
              ticker: fullTicker,
              description: bestMatch.Name,
              originalCode: bestMatch.Code,
              exchange: bestMatch.Exchange || 'US',
              type: bestMatch.Type,
              country: bestMatch.Country,
              currency: bestMatch.Currency,
              isin: bestMatch.ISIN,
            };
            dispatch(cacheAssetDetails(assetToCache));
          }
        });
      } catch (error) {
        console.error('Error fetching asset details:', error);
      }
    };


    const loadData = async () => {
      setIsLoading(true);
      try {
        window.scrollTo(0, 0);

        const loadedStrategy = await dispatch(fetchStrategyById({ id })).unwrap();


        if (loadedStrategy && loadedStrategy.assets) {
          await ensureAssetDetailsAreCached(loadedStrategy.assets);
        }

        try {

          await Promise.all([
            dispatch(fetchStrategies()).unwrap(),
            dispatch(fetchPublicStrategies()).unwrap()
          ]);

        } catch (error) {
          console.warn('Strategies list fetch failed:', error);
        }

      } catch (error) {
        console.error('error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  if (isLoading && !strategy) {
    return (
      <Grid container justifyContent="center" style={{ marginTop: '50px' }}>
        <CircularProgress />
      </Grid>
    );
  }



  return (
    <Grid container>
      <Grid container className={classes.head}>
        <Grid
          container
          flexDirection={isMobile ? "column" : "row"}
          flexWrap="nowrap"
          sx={{ padding: "16px 16px 16px 40px" }}
        >
          <Grid item xs={12} md={8}>
            <Typography
              variant="body3"
              style={{
                fontSize: isMobile ? "28px" : "36px",
                fontWeight: 600,
                lineHeight: "24px",
              }}
              gutterBottom
            >
              {currentStrategy?.name}
            </Typography>
          </Grid>

          <Grid
            container
            sx={{ marginTop: isMobile ? "20px" : "0px" }}
            justifyContent={isMobile ? "start" : "end"}
            flexDirection={isMobile ? 'column' : 'row'}
          >
            <Button
              variant="text"
              color="primary"
              style={{
                marginBottom: isMobile ? "0" : "16px",
                textTransform: "capitalize",
              }}
              onClick={() => setSettingsExpanded(!settingsExpanded)}
            >
              <span style={{ marginRight: "8px" }}>
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 23 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.6728 2.41501C10.4223 2.30353 10.1981 2.14066 10.0146 1.93696L9.07456 0.89996C8.80332 0.60179 8.44671 0.394557 8.05336 0.306513C7.66001 0.218469 7.24908 0.253906 6.87661 0.40799C6.50414 0.56207 6.18826 0.8273 5.97207 1.1675C5.75588 1.5077 5.64989 1.90631 5.66856 2.30896L5.73556 3.70996C5.75039 3.98426 5.70734 4.2586 5.6092 4.51517C5.51107 4.77174 5.36004 5.00479 5.16594 5.19917C4.97184 5.39355 4.73902 5.54492 4.48259 5.64343C4.22617 5.74194 3.95188 5.78539 3.67756 5.77096L2.27756 5.69996C1.87514 5.68154 1.47681 5.78763 1.13686 6.00378C0.796906 6.21993 0.531856 6.53563 0.377824 6.90787C0.22379 7.28011 0.188261 7.69078 0.276076 8.08395C0.363892 8.47711 0.570786 8.83364 0.868556 9.10496L1.91056 10.046C2.11399 10.2297 2.2766 10.454 2.38791 10.7044C2.49921 10.9549 2.55672 11.2259 2.55672 11.5C2.55672 11.7741 2.49921 12.0451 2.38791 12.2955C2.2766 12.546 2.11399 12.7703 1.91056 12.954L0.868556 13.894C0.568356 14.1649 0.359329 14.522 0.270157 14.9164C0.180984 15.3108 0.216037 15.7231 0.370504 16.0968C0.524976 16.4705 0.791286 16.7872 1.13291 17.0035C1.47453 17.2198 1.87472 17.3251 2.27856 17.305L3.67856 17.233C3.95294 17.218 4.2274 17.261 4.48409 17.359C4.74078 17.4571 4.97395 17.6081 5.16844 17.8022C5.36293 17.9963 5.51439 18.2292 5.61296 18.4857C5.71153 18.7422 5.75501 19.0166 5.74056 19.291L5.66856 20.691C5.64989 21.0936 5.75588 21.4922 5.97207 21.8324C6.18826 22.1726 6.50414 22.4379 6.87661 22.592C7.24908 22.746 7.66001 22.7815 8.05336 22.6934C8.44671 22.6054 8.80332 22.3982 9.07456 22.1L10.0146 21.059C10.1982 20.8556 10.4225 20.693 10.673 20.5816C10.9235 20.4703 11.1945 20.4128 11.4686 20.4128C11.7426 20.4128 12.0137 20.4703 12.2641 20.5816C12.5146 20.693 12.7389 20.8556 12.9226 21.059L13.8626 22.1C14.1337 22.3991 14.4907 22.6071 14.8846 22.6956C15.2786 22.784 15.6902 22.7486 16.0633 22.5942C16.4363 22.4397 16.7525 22.1738 16.9686 21.8328C17.1847 21.4917 17.2902 21.0923 17.2706 20.689L17.1986 19.289C17.1848 19.0153 17.2286 18.7417 17.3271 18.4859C17.4256 18.2302 17.5766 17.9979 17.7705 17.8042C17.9643 17.6104 18.1966 17.4595 18.4524 17.3611C18.7082 17.2627 18.9818 17.2191 19.2556 17.233L20.6556 17.305C21.0597 17.3258 21.4603 17.221 21.8024 17.0049C22.1445 16.7889 22.4114 16.4722 22.5662 16.0983C22.7211 15.7245 22.7564 15.3119 22.6673 14.9172C22.5782 14.5225 22.369 14.165 22.0686 13.894L21.0266 12.954C20.8231 12.7703 20.6605 12.546 20.5492 12.2955C20.4379 12.0451 20.3804 11.7741 20.3804 11.5C20.3804 11.2259 20.4379 10.9549 20.5492 10.7044C20.6605 10.454 20.8231 10.2297 21.0266 10.046L22.0686 9.10496C22.3684 8.834 22.577 8.47695 22.666 8.08274C22.7549 7.68854 22.7198 7.27647 22.5654 6.90303C22.4109 6.52958 22.1448 6.21302 21.8034 5.99675C21.4621 5.78047 21.0622 5.67506 20.6586 5.69496L19.2586 5.76596C18.9848 5.77988 18.7111 5.73621 18.4552 5.63776C18.1993 5.53931 17.967 5.38828 17.7731 5.19442C17.5792 5.00056 17.4282 4.76819 17.3298 4.51232C17.2313 4.25644 17.1876 3.98277 17.2016 3.70896L17.2736 2.30896C17.2944 1.90486 17.1896 1.50422 16.9735 1.1621C16.7574 0.81997 16.4407 0.55315 16.0669 0.398289C15.693 0.243429 15.2804 0.20813 14.8857 0.297243C14.491 0.386356 14.1336 0.59551 13.8626 0.89596L12.9226 1.93696C12.739 2.14066 12.5148 2.30353 12.2643 2.41501C12.0138 2.52649 11.7427 2.5841 11.4686 2.5841C11.1944 2.5841 10.9233 2.52649 10.6728 2.41501ZM18.1007 8.97567C18.3241 8.62687 18.2225 8.163 17.8737 7.93959C17.5249 7.71617 17.061 7.81781 16.8376 8.16661L14.9213 11.1583L13.1952 9.54518C13.0335 9.39406 12.8133 9.32208 12.5936 9.3485C12.3738 9.37492 12.177 9.49705 12.0557 9.6822L9.92633 12.9333L7.42633 11.1146C7.25311 10.9886 7.03428 10.9427 6.82502 10.9884C6.61576 11.0342 6.43603 11.1672 6.33116 11.3539L4.81516 14.0539C4.61237 14.4151 4.74076 14.8723 5.10194 15.0751C5.46311 15.2779 5.9203 15.1495 6.12309 14.7883L7.2261 12.8239L9.68093 14.6096C9.84694 14.7304 10.0552 14.7778 10.2571 14.7409C10.4591 14.7039 10.637 14.5858 10.7495 14.4141L12.8222 11.2496L14.555 12.8691C14.7177 13.0211 14.9395 13.093 15.1605 13.0653C15.3814 13.0376 15.5786 12.9132 15.6987 12.7257L18.1007 8.97567Z"
                    fill="#1976d2"
                  />
                </svg>
              </span>
              Settings
            </Button>
             <Button
              variant="text"
              color="primary"
              style={{
                marginBottom: isMobile ? "0" : "16px",
                marginLeft: isMobile ?  "0px" : '8px',
                textTransform: "capitalize",
              }}
              onClick={handleCopy}
            >
              <ContentCopyIcon sx={{ marginRight: "8px" }} />
              Copy Strategy
            </Button>

            <Button
              variant="text"
              color="primary"
              style={{
                marginBottom: isMobile ? "0" : "16px",
                marginLeft: isMobile ?  "0px" : '8px',
                textTransform: "capitalize",
              }}
              onClick={handleDelete}
            >
              <DeleteOutlinedIcon />
              Delete Strategy
            </Button>
          </Grid>
        </Grid>
        <hr />

        <Grid
          container
          justifyContent="center"
          sx={{
            backgroundImage: isMobile ? `` : `url(${investImage})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "100% 100%",
          }}
        >
          <StrategySettings
            settingsExpanded={settingsExpanded}
            setSettingsExpanded={setSettingsExpanded}
            strategy={currentStrategy}
            assetStartYear={currentStrategy.start_year}
            assetEndYear={currentStrategy.end_year}
            snackbarOpen={snackbarOpen}
            snackbarMessage={snackbarMessage}
            snackbarSeverity={snackbarSeverity}
            onSnackbarClose={() => setSnackbarOpen(false)}
            onStrategyAction={(message, severity) => {
              setSnackbarMessage(message);
              setSnackbarSeverity(severity);
              setSnackbarOpen(true);
            }}
          />
        </Grid>
      </Grid>

      {!isMobile && (
        <Grid
          item
          xs={12}
          ref={tabsRef}
          className={clsx({ [classes.fixedTabs]: isFixed })}
        >
          <Paper elevation={1}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              className={classes.tabs}
              variant="fullWidth"
            >
              <Tab label="Overview" />
              <Tab label="Performance" />
              <Tab label="Downside Risk" />
              <Tab label="Risk & Return" />
              <Tab label="Cladogram" />
              <Tab label="Monte Carlo" />
            </Tabs>
          </Paper>
        </Grid>
      )}

      <StrategyContent
        strategy={currentStrategy}
        zoomOptions={zoomOptions}
        strategyOverviewData={strategyOverviewData}
        performanceData={performanceData}
        strategyPerformanceData={strategyPerformanceData}
        monteCarloData={monteCarloData}
        monteCarloDataNew={monteCarloDataNew}
        drawdownData={drawdownData}
        assets={assets}
        strategyId={currentStrategy.id}
      />
    </Grid>
  );
};

export default StrategyDetails;
